import type { Element, Pillar, PillarKey, SajuResult, TenGod } from './types';

export type TenGodGroupId = 'peer' | 'output' | 'wealth' | 'authority' | 'resource';
export type ShinsalId = 'nobleman' | 'peachBlossom' | 'travel' | 'canopy';

export interface TenGodGroup {
  id: TenGodGroupId;
  label: string;
  gods: TenGod[];
  weight: number;
  /** Share of observed ten-god weights, not a personality or probability score. */
  share: number;
  keywords: string;
}

export interface YongshinReference {
  status: 'reference' | 'partial';
  strength: 'strong' | 'balanced' | 'weak' | 'undetermined';
  title: string;
  /** Korean element labels for display; null means no single candidate is proposed. */
  primary: string | null;
  candidates: string[];
  seasonalCandidate: string | null;
  /** Share of same-element + resource weights. This is not confidence. */
  supportShare: number;
  weightedElements: { element: string; weight: number }[];
  method: string;
  reasons: string[];
  caveat: string;
}

export interface ShinsalMatch {
  reference: '일간' | '일지' | '연지';
  referenceValue: string;
  targetBranch: string;
  pillar: PillarKey;
  pillarLabel: string;
}

export interface ShinsalResult {
  id: ShinsalId;
  name: string;
  description: string;
  rule: string;
  matches: ShinsalMatch[];
  sourceUrl: string;
}

export interface SajuReport {
  id: 'personality' | 'love' | 'career' | 'wealth';
  label: string;
  title: string;
  body: string;
  reasons: string[];
  action: string;
}

export interface SajuInterpretation {
  tenGodGroups: TenGodGroup[];
  yongshin: YongshinReference;
  /** Includes unmatched rules so that an empty match is distinguishable from an unchecked rule. */
  shinsal: ShinsalResult[];
  reports: SajuReport[];
  caveat: string;
  sources: { title: string; url: string }[];
}

const ELEMENTS: Element[] = ['wood', 'fire', 'earth', 'metal', 'water'];
const ELEMENT_NAMES: Record<Element, string> = { wood: '목', fire: '화', earth: '토', metal: '금', water: '수' };
const BRANCH_NAMES = ['자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술', '해'];
const PILLAR_KEYS: PillarKey[] = ['year', 'month', 'day', 'hour'];
const SOURCE_TWO = 'https://zh.wikisource.org/zh-hant/三命通會/卷二';
const SOURCE_THREE = 'https://zh.wikisource.org/zh-hant/三命通會/卷三';
const SOURCE_FIVE = 'https://zh.wikisource.org/zh-hant/三命通會/卷五';

export const INTERPRETATION_SOURCES = [
  { title: '삼명통회 권2 · 오행의 계절·화개·도화', url: SOURCE_TWO },
  { title: '삼명통회 권3 · 천을귀인·역마 대조표', url: SOURCE_THREE },
  { title: '삼명통회 권5 · 일간과 십성의 생극 관계', url: SOURCE_FIVE },
];

const GROUPS: Omit<TenGodGroup, 'weight' | 'share'>[] = [
  { id: 'peer', label: '비겁', gods: ['비견', '겁재'], keywords: '자기 기준 · 동료 · 주도성' },
  { id: 'output', label: '식상', gods: ['식신', '상관'], keywords: '표현 · 창작 · 실행' },
  { id: 'wealth', label: '재성', gods: ['편재', '정재'], keywords: '자원 관리 · 현실 감각 · 성과' },
  { id: 'authority', label: '관성', gods: ['편관', '정관'], keywords: '책임 · 규칙 · 역할' },
  { id: 'resource', label: '인성', gods: ['편인', '정인'], keywords: '학습 · 숙고 · 회복' },
];

/**
 * Transparent service heuristic, not an ancient numeric standard:
 * a branch totals 1; principal qi gets .6 and the remainder shares .4 equally.
 * A single hidden stem gets 1. We do not infer an unsupported middle/residual order.
 */
function hiddenWeights(pillar: Pillar): { element: Element; god: TenGod; weight: number }[] {
  return pillar.hiddenStems.map((hidden) => ({
    element: hidden.stem.element,
    god: hidden.tenGod,
    weight: pillar.hiddenStems.length === 1 ? 1 : hidden.role === 'principal' ? 0.6 : 0.4 / (pillar.hiddenStems.length - 1),
  }));
}

function knownPillars(chart: SajuResult): Pillar[] {
  return PILLAR_KEYS.map((key) => chart.pillars[key]).filter((pillar): pillar is Pillar => pillar !== null && pillar.certain);
}

function tenGodGroups(chart: SajuResult): TenGodGroup[] {
  const counts = Object.fromEntries(GROUPS.map((group) => [group.id, 0])) as Record<TenGodGroupId, number>;
  const add = (god: TenGod, weight: number) => {
    const group = GROUPS.find((entry) => entry.gods.includes(god));
    if (group) counts[group.id] += weight;
  };
  for (const pillar of knownPillars(chart)) {
    if (pillar.tenGod) add(pillar.tenGod, 1);
    for (const hidden of hiddenWeights(pillar)) add(hidden.god, hidden.weight);
  }
  const total = Object.values(counts).reduce((sum, value) => sum + value, 0);
  return GROUPS.map((group) => ({ ...group, gods: [...group.gods], weight: counts[group.id], share: total ? counts[group.id] / total * 100 : 0 }));
}

const YONGSHIN_METHOD = '서비스 참고식: 천간 각 1, 지지 각 1(본기 0.6·나머지 균등 0.4, 단일 지장간은 1), 월지는 2배로 계산합니다. 일간을 포함하며 같은 오행과 일간을 생하는 오행의 합이 60% 이상이면 강한 쪽, 40% 미만이면 약한 쪽, 그 사이는 중간으로 봅니다. 숫자와 경계값은 설명을 위한 서비스 규칙이며 고전의 표준 점수나 확률이 아닙니다.';
const YONGSHIN_CAVEAT = '용신은 학파·격국·조후·합충과 생극의 흐름에 따라 달라집니다. 이 결과는 확정 용신이 아닌 단순 억부 참고이며, 종격·합화·지장간 사령 일수·대운과 세운을 판정하지 않습니다. 적게 나온 오행을 곧바로 용신으로 정하지 않습니다.';

function yongshinReference(chart: SajuResult): YongshinReference {
  const weights: Record<Element, number> = { wood: 0, fire: 0, earth: 0, metal: 0, water: 0 };
  const base: YongshinReference = {
    status: 'partial', strength: 'undetermined', title: '용신 참고를 보류했어요', primary: null, candidates: [], seasonalCandidate: null,
    supportShare: 0, weightedElements: [], method: YONGSHIN_METHOD, reasons: [], caveat: YONGSHIN_CAVEAT,
  };
  if (!chart.metadata.interpretationAllowed) {
    return { ...base, reasons: ['태어난 시각에 따라 년주·월주·일주가 달라질 수 있어 일간과 월령을 확정할 수 없습니다. 출생 시각을 확인한 뒤 다시 계산해 주세요.'] };
  }
  for (const pillar of knownPillars(chart)) {
    weights[pillar.stem.element] += 1;
    const monthWeight = pillar.key === 'month' ? 2 : 1;
    for (const hidden of hiddenWeights(pillar)) weights[hidden.element] += hidden.weight * monthWeight;
  }
  const dayIndex = ELEMENTS.indexOf(chart.dayMaster.element);
  const self = chart.dayMaster.element;
  const resource = ELEMENTS[(dayIndex + 4) % 5];
  const output = ELEMENTS[(dayIndex + 1) % 5];
  const wealth = ELEMENTS[(dayIndex + 2) % 5];
  const authority = ELEMENTS[(dayIndex + 3) % 5];
  const total = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
  const supportShare = (weights[self] + weights[resource]) / total * 100;
  const weightedElements = ELEMENTS.map((element) => ({ element: ELEMENT_NAMES[element], weight: weights[element] }));
  const month = chart.pillars.month.branch;
  const reasons = [
    `${chart.dayMaster.korean}${ELEMENT_NAMES[self]} 일간을 돕는 ${ELEMENT_NAMES[self]}·${ELEMENT_NAMES[resource]}의 참고 가중치 비중은 ${supportShare.toFixed(1)}%입니다.`,
    `절기 기준 월지는 ${month.korean}(${month.hanja})이며, 계절의 영향을 반영하기 위해 월지 지장간에 2배의 비중을 주었습니다.`,
  ];
  if (!chart.metadata.timeKnown) {
    return { ...base, supportShare, weightedElements, reasons: [...reasons, '시주가 빠진 세 기둥의 중간 집계입니다. 강약 분류와 용신 후보는 시각을 확인할 때까지 보류합니다.'] };
  }
  const strength = supportShare >= 60 ? 'strong' : supportShare < 40 ? 'weak' : 'balanced';
  const candidates = strength === 'strong' ? [output, wealth, authority] : strength === 'weak' ? [resource, self] : [];
  const winter = [11, 0, 1].includes(month.index);
  const summer = [5, 6, 7].includes(month.index);
  const seasonalCandidate = winter ? '화' : summer ? '수' : null;
  if (strength === 'strong') reasons.push('돕는 기운이 많은 쪽으로 집계되어, 일간의 기운을 표현으로 내보내는 식상 오행을 첫 후보로, 재성·관성을 추가 검토 후보로 표시합니다.');
  else if (strength === 'weak') reasons.push('돕는 기운이 적은 쪽으로 집계되어, 일간을 생하는 인성 오행을 첫 후보로, 같은 오행인 비겁을 추가 검토 후보로 표시합니다.');
  else reasons.push('이 참고식의 중간 범위에 있어 하나의 용신 후보를 고르지 않습니다. 월령과 원국 전체의 작용을 더 살펴야 합니다.');
  if (seasonalCandidate) reasons.push(`${winter ? '해·자·축의 겨울' : '사·오·미의 여름'} 월지이므로 계절상 ${seasonalCandidate}도 검토할 수 있습니다. 이는 조후의 출발점이며, 억부 후보와 다를 수 있어 최종 용신으로 합치지 않습니다.`);
  return {
    ...base, status: 'reference', strength,
    title: strength === 'strong' ? '기운을 표현하고 나누는 방향' : strength === 'weak' ? '기반을 채우고 돕는 방향' : '하나로 단정하지 않는 균형 구간',
    primary: candidates[0] ? ELEMENT_NAMES[candidates[0]] : null,
    candidates: candidates.map((element) => ELEMENT_NAMES[element]), seasonalCandidate, supportShare, weightedElements, reasons,
  };
}

// 三命通會 卷三 論天乙貴人: 甲戊庚 -> 丑未; 乙己 -> 子申;
// 丙丁 -> 酉亥; 壬癸 -> 卯巳; 辛 -> 寅午. Day stem is this service's reference.
const NOBLEMAN: number[][] = [[1, 7], [0, 8], [9, 11], [9, 11], [1, 7], [0, 8], [1, 7], [2, 6], [3, 5], [3, 5]];
// Explicit table in 子丑寅卯辰巳午未申酉戌亥 order. The 三合 groups are
// 申子辰 / 巳酉丑 / 寅午戌 / 亥卯未. No modulo shortcut obscures the source mapping.
const PEACH_BLOSSOM = [9, 6, 3, 0, 9, 6, 3, 0, 9, 6, 3, 0];
const TRAVEL = [2, 11, 8, 5, 2, 11, 8, 5, 2, 11, 8, 5];
const CANOPY = [4, 1, 10, 7, 4, 1, 10, 7, 4, 1, 10, 7];

function shinsalResults(chart: SajuResult): ShinsalResult[] {
  const pillars = knownPillars(chart);
  const stemMatches: ShinsalMatch[] = [];
  for (const pillar of pillars) {
    if (NOBLEMAN[chart.dayMaster.index]?.includes(pillar.branch.index)) stemMatches.push({
      reference: '일간', referenceValue: chart.dayMaster.korean, targetBranch: pillar.branch.korean, pillar: pillar.key, pillarLabel: pillar.label,
    });
  }
  const branchMatches = (table: number[]): ShinsalMatch[] => {
    const matches: ShinsalMatch[] = [];
    for (const referenceKey of ['day', 'year'] as const) {
      const reference = chart.pillars[referenceKey].branch;
      const target = table[reference.index];
      for (const pillar of pillars) if (pillar.branch.index === target) matches.push({
        reference: referenceKey === 'day' ? '일지' : '연지', referenceValue: reference.korean, targetBranch: BRANCH_NAMES[target], pillar: pillar.key, pillarLabel: pillar.label,
      });
    }
    return matches;
  };
  const scope = '연지와 일지에 각각 대조표를 적용하고 기준 기둥을 포함한 모든 알려진 지지에서 찾습니다. 같은 지지가 두 기준에 해당하면 근거를 각각 표시합니다.';
  return [
    { id: 'nobleman', name: '천을귀인', description: '전통적으로 도움과 협력을 떠올리는 상징입니다. 실제 조력자의 존재나 성공을 보장하지 않습니다.', rule: '일간 기준: 갑·무·경→축·미, 을·기→자·신, 병·정→유·해, 임·계→묘·사, 신→인·오. 원국의 모든 알려진 지지에서 찾으며 주야 귀인을 나누지 않습니다.', matches: stemMatches, sourceUrl: SOURCE_THREE },
    { id: 'peachBlossom', name: '도화', description: '표현과 사람 사이의 교류를 돌아보는 상징으로 읽습니다. 외모·인기·관계의 성패를 단정하지 않습니다.', rule: `신·자·진→유, 사·유·축→오, 인·오·술→묘, 해·묘·미→자. ${scope}`, matches: branchMatches(PEACH_BLOSSOM), sourceUrl: SOURCE_TWO },
    { id: 'travel', name: '역마', description: '이동과 새로운 환경에 대한 관심을 돌아보는 상징으로 읽습니다. 이사나 이직이 일어난다는 예언은 아닙니다.', rule: `신·자·진→인, 사·유·축→해, 인·오·술→신, 해·묘·미→사. ${scope}`, matches: branchMatches(TRAVEL), sourceUrl: SOURCE_THREE },
    { id: 'canopy', name: '화개', description: '몰입과 탐구, 혼자 생각하는 시간을 돌아보는 상징으로 읽습니다. 외로움이나 특정 진로를 정하는 표시는 아닙니다.', rule: `신·자·진→진, 사·유·축→축, 인·오·술→술, 해·묘·미→미. ${scope}`, matches: branchMatches(CANOPY), sourceUrl: SOURCE_TWO },
  ];
}

const ELEMENT_THEME: Record<Element, { title: string; body: string; action: string }> = {
  wood: { title: '자라는 방향을 찾는 사람', body: '목 일간은 성장과 확장의 상징으로 읽습니다. 새로운 것을 배우고 오래 키워 가는 일에서 나다운 방식을 찾아보세요.', action: '지금 키우고 싶은 일 하나와 그 일을 꾸준히 할 수 있는 작은 습관을 적어 보세요.' },
  fire: { title: '생각을 밖으로 밝히는 사람', body: '화 일간은 표현과 온기의 상징으로 읽습니다. 생각을 드러내는 순간과 차분히 쉬는 순간의 리듬을 살펴보세요.', action: '오늘 전하고 싶은 마음을 한 문장으로 쓰고, 상대가 듣기 편한 때에 전해 보세요.' },
  earth: { title: '중심을 만들고 쌓는 사람', body: '토 일간은 안정과 조율의 상징으로 읽습니다. 지키고 싶은 기준과 변화해도 괜찮은 부분을 구분해 보세요.', action: '맡은 일 중 꼭 지킬 약속 하나와 다른 사람에게 나눌 수 있는 일 하나를 골라 보세요.' },
  metal: { title: '기준을 다듬고 완성하는 사람', body: '금 일간은 구분과 정돈의 상징으로 읽습니다. 분명한 기준이 도움이 되는 때와 유연함이 필요한 때를 살펴보세요.', action: '최근 내린 판단 하나를 떠올리고, 그 판단의 근거와 바꿀 수 있는 조건을 적어 보세요.' },
  water: { title: '흐름을 읽고 연결하는 사람', body: '수 일간은 관찰과 유연함의 상징으로 읽습니다. 충분히 생각하는 시간과 작은 결정을 실행하는 시간을 함께 마련해 보세요.', action: '모아 둔 생각 중 하나를 고르고, 오늘 확인할 수 있는 작은 질문으로 바꿔 보세요.' },
};

const LOVE: Record<TenGodGroupId, { title: string; body: string; action: string }> = {
  peer: { title: '서로의 기준을 말로 맞추기', body: '비겁의 주도성과 동료라는 상징을 관계에 비추면, 각자의 독립성을 존중하는 대화가 점검 주제가 됩니다. 내 방식과 상대의 방식 사이에서 합의할 부분을 찾아보세요.', action: '연락 빈도나 혼자 쉬는 시간처럼 서로에게 중요한 기준 하나를 물어보세요.' },
  output: { title: '표현한 만큼 듣는 시간 갖기', body: '식상의 표현이라는 상징을 관계에 비추면, 마음을 전달하는 방법이 점검 주제가 됩니다. 말이나 이벤트뿐 아니라 상대의 답을 충분히 듣는 시간도 마련해 보세요.', action: '고마웠던 일 한 가지를 구체적으로 말하고, 상대가 요즘 원하는 배려를 물어보세요.' },
  wealth: { title: '일상에서 배려를 나누기', body: '재성의 자원 관리라는 상징을 관계에 비추면, 시간과 일상의 수고를 나누는 방식이 점검 주제가 됩니다. 배려를 수치로 평가하기보다 서로에게 필요한 지원을 확인해 보세요.', action: '함께 쓰는 시간이나 맡은 일 중 한 가지를 골라, 두 사람 모두 편한 방식인지 이야기해 보세요.' },
  authority: { title: '약속과 유연함 함께 챙기기', body: '관성의 책임이라는 상징을 관계에 비추면, 믿을 수 있는 약속이 점검 주제가 됩니다. 좋은 관계의 모습에 대한 기준을 상대에게 일방적으로 적용하고 있지는 않은지 살펴보세요.', action: '지키고 싶은 약속 하나와 상황에 따라 조정할 수 있는 약속 하나를 함께 정해 보세요.' },
  resource: { title: '생각 속 마음을 밖으로 나누기', body: '인성의 숙고라는 상징을 관계에 비추면, 혼자 이해한 마음을 말로 확인하는 과정이 점검 주제가 됩니다. 상대의 의도를 짐작하는 대신 궁금한 점을 직접 물어보세요.', action: '마음속에서만 생각한 바람 하나를 부담 없는 요청으로 바꿔 전해 보세요.' },
};

const CAREER: Record<TenGodGroupId, { title: string; body: string; action: string }> = {
  peer: { title: '주도할 범위가 선명한가요', body: '비겁의 자기 기준이라는 상징에서 출발해, 다음 직장에서 직접 결정할 수 있는 범위와 협업 방식을 점검해 보세요. 이직의 적기는 원국만으로 정하지 않습니다.', action: '현재와 관심 직장의 의사결정 범위, 협업 방식, 근무 조건을 같은 항목으로 비교해 보세요.' },
  output: { title: '내 결과물을 보여 줄 수 있나요', body: '식상의 표현과 실행이라는 상징에서 출발해, 아이디어를 실제 결과물로 만들 수 있는 업무인지 살펴보세요. 이직 가능성은 경력과 채용 조건을 함께 확인해야 합니다.', action: '지원하고 싶은 역할의 요구사항 하나를 골라, 내 경험으로 보여 줄 수 있는 결과물을 정리해 보세요.' },
  wealth: { title: '성과의 기준이 명확한가요', body: '재성의 성과와 자원 관리라는 상징에서 출발해, 무엇을 잘했을 때 평가받는 역할인지 점검해 보세요. 원국으로 연봉 상승이나 합격을 예측할 수는 없습니다.', action: '업무 범위, 성과 평가 방식, 실제 보상 조건을 채용 공고와 면담에서 확인해 보세요.' },
  authority: { title: '책임과 권한이 맞물리나요', body: '관성의 책임과 역할이라는 상징에서 출발해, 기대되는 책임에 맞는 권한과 지원이 주어지는지 살펴보세요. 안정감은 직함보다 실제 업무 조건으로 확인하는 편이 좋습니다.', action: '입사 후 첫 세 달의 목표, 의사결정 권한, 지원받을 자원을 구체적으로 질문해 보세요.' },
  resource: { title: '배움이 일로 이어지나요', body: '인성의 학습과 숙고라는 상징에서 출발해, 쌓은 지식을 적용하고 피드백받을 수 있는 환경인지 살펴보세요. 준비를 충분히 했는지는 실제 직무 요건과 비교해 확인해 보세요.', action: '관심 직무에서 이미 갖춘 역량과 확인이 필요한 역량을 하나씩 적어 보세요.' },
};

function reports(chart: SajuResult, groups: TenGodGroup[], yongshin: YongshinReference): SajuReport[] {
  const largestWeight = Math.max(...groups.map((group) => group.weight));
  const leaders = groups.filter((group) => Math.abs(group.weight - largestWeight) < 1e-9);
  const tied = leaders.length > 1;
  const lead = leaders[0];
  const leadReason = `${leaders.map((group) => `${group.label} ${group.share.toFixed(1)}%`).join(' · ')}${tied ? '로 공동 최다입니다. 한 성향으로 우선순위를 정하지 않습니다.' : '로 십성 참고 집계에서 가장 큰 비중입니다.'}`;
  const countReason = '십성 비중은 일간을 제외한 천간 각 1과 지장간 각 지지 합계 1(본기 0.6·나머지 균등 0.4, 단일 지장간 1)을 합산합니다. 없는 십성도 능력의 부재를 뜻하지 않습니다.';
  const partialReason = chart.metadata.timeKnown ? [] : ['시각 미상으로 시주를 제외한 부분 해석입니다. 시주가 추가되면 비중과 문장이 달라질 수 있습니다.'];
  const theme = ELEMENT_THEME[chart.dayMaster.element];
  const wealth = groups.find((group) => group.id === 'wealth')!;
  const wealthPresent = wealth.weight > 0;
  const balanceReason = yongshin.strength === 'undetermined'
    ? '시주가 없어 강약과 용신의 추천은 보류했습니다.'
    : yongshin.strength === 'balanced'
      ? '억부 참고식에서는 중간 범위로 집계되어 특정 보완 오행을 정하지 않았습니다.'
      : `억부 참고식의 방향은 ‘${yongshin.title}’입니다. 확정된 성격 진단은 아닙니다.`;
  const love = tied ? {
    title: '서로 다른 관계의 기준 살펴보기',
    body: `${leaders.map((group) => group.label).join('·')}의 비중이 같아 한 가지 관계 유형으로 좁히지 않습니다. 표현, 약속, 개인의 시간을 어떤 비율로 원하는지 실제 대화로 확인해 보세요.`,
    action: '관계에서 원하는 것 두 가지를 적고, 상대의 우선순위와 어떻게 다른지 물어보세요.',
  } : LOVE[lead.id];
  const career = tied ? {
    title: '조건을 나란히 놓고 선택하기',
    body: `${leaders.map((group) => group.label).join('·')}이 함께 관찰되어 하나의 직업 적성으로 정하지 않습니다. 이직은 업무의 흥미, 근무 조건, 성장 기회처럼 현실에서 비교할 수 있는 항목으로 검토해 보세요.`,
    action: '현재 직장과 관심 직장의 장단점을 같은 세 가지 기준으로 적어 보세요.',
  } : CAREER[lead.id];
  return [
    { id: 'personality', label: '성향', title: theme.title, body: `${theme.body} ${tied ? '십성의 여러 주제가 함께 나타나므로 한 가지 유형으로 좁히지 않습니다.' : `${lead.label}은 ${lead.keywords.replaceAll(' · ', ', ')}의 주제로 함께 읽어볼 수 있습니다.`}`, reasons: [`일간은 ${chart.dayMaster.korean}(${chart.dayMaster.hanja}), ${chart.dayMaster.yinYang === 'yang' ? '양' : '음'}의 ${ELEMENT_NAMES[chart.dayMaster.element]}입니다.`, leadReason, balanceReason, ...partialReason], action: theme.action },
    { id: 'love', label: '연애', ...love, reasons: [leadReason, countReason, '성별·결혼 여부를 추정하지 않으며, 상대의 원국이나 관계의 실제 맥락은 반영하지 않았습니다.', ...partialReason] },
    { id: 'career', label: '이직', ...career, reasons: [leadReason, countReason, '대운·세운과 채용 상황을 계산하지 않으므로 이직 날짜·합격 여부를 예측하지 않습니다.', ...partialReason] },
    { id: 'wealth', label: '재물', title: wealthPresent ? '돈보다 관리하는 방식을 보기' : '기록으로 내 자원 감각 살피기', body: wealthPresent ? '재성은 전통적으로 자원과 관리의 상징입니다. 이 원국에서 재성이 관찰된다는 점을, 시간과 돈을 어디에 배분하고 있는지 돌아보는 질문으로 활용해 보세요.' : '이 집계에서 재성의 비중이 없다고 재물이나 관리 능력이 없다는 뜻은 아닙니다. 원국의 기호보다 실제 수입·지출 기록을 통해 내가 자원을 쓰는 방식을 확인해 보세요.', reasons: [`재성(정재·편재)의 십성 참고 비중은 ${wealth.share.toFixed(1)}%입니다. 금액이나 수익률을 의미하지 않습니다.`, countReason, '재물의 많고 적음, 투자 성과, 특정 시기의 수입을 예측하지 않습니다.', ...partialReason], action: '최근 한 달간 계획한 지출과 실제 지출을 비교하고, 내가 중요하게 여기는 곳에 자원을 쓰고 있는지 살펴보세요.' },
  ];
}

/** Pure deterministic interpretation. No random scores, external calls, gender assumptions or annual fortune predictions. */
export function buildSajuInterpretation(chart: SajuResult): SajuInterpretation {
  const yongshin = yongshinReference(chart);
  if (!chart.metadata.interpretationAllowed) return {
    tenGodGroups: [], yongshin, shinsal: [], reports: [],
    caveat: '절기 또는 날짜 경계에서 출생 시각을 몰라 원국을 확정할 수 없습니다. 시각을 확인하기 전에는 십성 분포·용신·신살·풀이를 보류합니다.',
    sources: INTERPRETATION_SOURCES.map((source) => ({ ...source })),
  };
  const groups = tenGodGroups(chart);
  return {
    tenGodGroups: groups, yongshin, shinsal: shinsalResults(chart), reports: reports(chart, groups, yongshin),
    caveat: `${chart.metadata.timeKnown ? '' : '시주를 제외한 부분 결과입니다. 신살도 알려진 기둥에서만 확인합니다. '}전통 명리의 상징을 활용한 자기 성찰용 참고입니다. 검증된 성격 진단이나 미래 예측이 아니며, 신살이 표시되지 않아도 기회나 능력이 없다는 뜻은 아닙니다.`,
    sources: INTERPRETATION_SOURCES.map((source) => ({ ...source })),
  };
}
